import { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import Button from '../common/Button.js';

function relativeAgo(iso, nowMs) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? '1 min ago' : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

const LoginOtpModal = ({
  open,
  challenge,
  verifying = false,
  resending = false,
  error = '',
  onClose,
  onVerify,
  onResend,
}) => {
  const [otp, setOtp] = useState('');
  const [resendWait, setResendWait] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const submittedRef = useRef('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setOtp('');
      submittedRef.current = '';
      setResendWait(0);
      return;
    }
    setOtp('');
    submittedRef.current = '';
    setResendWait(Number(challenge?.resend_available_in_seconds) || 0);
    setNowMs(Date.now());
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, challenge?.challenge_id]);

  useEffect(() => {
    if (!open) return undefined;
    const nextWait = Number(challenge?.resend_available_in_seconds);
    if (Number.isFinite(nextWait) && nextWait > 0) {
      setResendWait(nextWait);
    }
    return undefined;
  }, [open, challenge?.resend_available_in_seconds, challenge?.challenge_id]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => {
      setResendWait((prev) => (prev > 0 ? prev - 1 : 0));
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (error) {
      submittedRef.current = '';
    }
  }, [error]);

  useEffect(() => {
    if (resending) {
      setOtp('');
      submittedRef.current = '';
    }
  }, [resending]);

  useEffect(() => {
    if (!open || !('OTPCredential' in window) || !navigator.credentials?.get) {
      return undefined;
    }
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((cred) => {
        const code = String(cred?.code || '').replace(/\D/g, '').slice(0, 6);
        if (code) setOtp(code);
      })
      .catch(() => {});
    return () => ac.abort();
  }, [open, challenge?.challenge_id]);

  const submitOtp = useCallback(
    (value) => {
      const code = String(value || '').replace(/\D/g, '').slice(0, 6);
      if (code.length !== 6 || verifying) return;
      if (submittedRef.current === code) return;
      submittedRef.current = code;
      onVerify?.(code);
    },
    [onVerify, verifying],
  );

  const handleChange = (value) => {
    const next = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (next !== submittedRef.current) {
      submittedRef.current = '';
    }
    setOtp(next);
    if (next.length === 6) {
      submitOtp(next);
    }
  };

  if (!open) return null;

  const masked = challenge?.masked_phone || 'your WhatsApp';
  const expiresIn = Number(challenge?.expires_in_seconds) || 0;
  const requestedLabel = relativeAgo(challenge?.requested_at, nowMs);
  const minutesLeft = expiresIn ? Math.max(1, Math.ceil(expiresIn / 60)) : 0;

  return (
    <div className="fixed inset-0 z-[120] flex items-end md:items-center justify-center bg-black/50 p-0 md:p-6">
      <div
        className="bg-white w-full max-w-md md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl pb-[env(safe-area-inset-bottom)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-otp-title"
      >
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 md:p-6 border-b border-gray-100">
          <div className="min-w-0">
            <h3 id="login-otp-title" className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 shrink-0 text-[#3467ae]" />
              Verify login OTP
            </h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">
              Code sent on WhatsApp to {masked}. Enter the 6 digits to sign in.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-1 rounded-lg text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close"
            disabled={verifying}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 md:p-6 space-y-4">
          {(requestedLabel || minutesLeft > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
              {requestedLabel && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
                  <p className="text-gray-500">Requested</p>
                  <p className="font-medium text-gray-900 mt-0.5">{requestedLabel}</p>
                </div>
              )}
              {requestedLabel && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
                  <p className="text-gray-500">Sent on WhatsApp</p>
                  <p className="font-medium text-gray-900 mt-0.5">{requestedLabel}</p>
                </div>
              )}
              {minutesLeft > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
                  <p className="text-gray-500">Valid for</p>
                  <p className="font-medium text-gray-900 mt-0.5">
                    {minutesLeft === 1 ? '1 min' : `${minutesLeft} min`}
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            name="otp"
            value={otp}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="••••••"
            maxLength={6}
            disabled={verifying}
            aria-label="6-digit OTP"
            className="w-full min-h-[56px] px-3 py-3 text-2xl tracking-[0.45em] text-center border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3467ae]"
          />

          <Button
            type="button"
            variant="primary"
            fullWidth
            onClick={() => submitOtp(otp)}
            loading={verifying}
            disabled={verifying || otp.length !== 6}
            className="min-h-[48px]"
          >
            {verifying ? 'VERIFYING...' : 'VERIFY & SIGN IN'}
          </Button>

          <button
            type="button"
            onClick={onResend}
            disabled={resending || verifying || resendWait > 0}
            className="w-full min-h-[44px] text-sm text-[#3467ae] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {resending
              ? 'Resending...'
              : resendWait > 0
                ? `Resend OTP in ${resendWait}s`
                : 'Resend OTP'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginOtpModal;
